import React, { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import Editor from "@monaco-editor/react";
import type { editor } from "monaco-editor";
import { inputClasses, buttonClasses } from "../common/tailwindClassnames";
import { SegmentedToggle } from "../common/SegmentedToggle";
import { GUNDI_OBJECT_SPECS } from "./schema-builder/gundiObjectSpecs";
import { validateMappedOutput } from "./schema-builder/validation";
import { SelectMenu } from "./schema-builder/SelectMenu";
import { registerJqLanguage } from "./jqLanguage";
import { CoreFieldMaps, AdditionalField, Condition, OPS, ParseResult } from "./jq-filter-builder/types";
import { jqPath, valAt, flatPaths, genJq, evalMapping, parseJqToBuilderState } from "./jq-filter-builder/helpers";
import { PANEL_CLASSES, HEADER_CLASSES, SECTION_LABEL, MAPPING_DISPLAY, MAPPING_DISPLAY_WARNING, DROP_ZONE, DROP_ZONE_ACTIVE, DROP_ZONE_IDLE } from "./jq-filter-builder/styles";

// ── Drag-and-drop helpers ──

type DragPathRef = React.MutableRefObject<string[] | null>;

// Keyboard-mapping handshake: a drop slot calls this on Enter to hand its "pick" callback
// to the parent, which then moves focus into the source tree. Selecting a leaf (Enter)
// invokes the callback with that leaf's path. Mirrors the drag-and-drop flow for keyboards.
type StartPick = (onPick: (path: string[]) => void, slotEl: HTMLElement) => void;

function useDragOver(readOnly: boolean) {
    const [isOver, setIsOver] = useState(false);

    const handlers = readOnly ? {} : {
        onDragOver: (e: React.DragEvent) => { e.preventDefault(); e.dataTransfer.dropEffect = "copy"; setIsOver(true); },
        onDragLeave: () => setIsOver(false),
    };

    const resetOver = () => setIsOver(false);

    return { isOver, handlers, resetOver };
}

// ── Sub-components ──

function TreeNode({ name, value, path, depth, collapsed, onToggle, readOnly, dragPathRef, onLeafActivate, pickActive }: {
    name: string;
    value: any;
    path: string[];
    depth: number;
    collapsed: Set<string>;
    onToggle: (key: string) => void;
    readOnly: boolean;
    dragPathRef: DragPathRef;
    onLeafActivate?: (path: string[]) => void;
    pickActive?: boolean;
}) {
    const { t } = useTranslation();
    const isObject = value != null && typeof value === "object" && !Array.isArray(value);
    const isArray = Array.isArray(value);
    const isLeaf = !isObject && !isArray;
    const pathKey = path.join(".");
    const hasChildren = isObject ? Object.keys(value as object).length > 0
        : isArray ? (value as any[]).length > 0
            : false;

    const typeColor = typeof value === "number" ? "#6d97f7"
        : typeof value === "string" ? "#007a0b"
            : typeof value === "boolean" ? "#d6520e"
                : "#8f8f8f";

    const displayValue = isObject ? `{${Object.keys(value as object).length}}`
        : isArray ? `[${(value as any[]).length}]`
            : JSON.stringify(value);

    const ghostRef = useRef<HTMLDivElement | null>(null);

    const handleDragStart = (e: React.DragEvent) => {
        if (!isLeaf || readOnly) return;
        dragPathRef.current = path;
        e.dataTransfer.setData("text/plain", pathKey);
        e.dataTransfer.effectAllowed = "copy";

        const ghost = document.createElement("div");
        ghost.textContent = jqPath(path);
        ghost.style.cssText = "position:absolute;top:-100px;left:-100px;padding:4px 10px;background:#00520c;color:#fff;border-radius:6px;font:12px monospace;white-space:nowrap;";
        document.body.appendChild(ghost);
        ghostRef.current = ghost;
        e.dataTransfer.setDragImage(ghost, 0, 0);
    };

    const handleDragEnd = () => {
        dragPathRef.current = null;
        if (ghostRef.current) {
            ghostRef.current.remove();
            ghostRef.current = null;
        }
    };

    useEffect(() => {
        return () => { ghostRef.current?.remove(); };
    }, []);

    return (
        <div>
            <div
                draggable={isLeaf && !readOnly}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                tabIndex={isLeaf && !readOnly ? (pickActive ? 0 : -1) : undefined}
                role={isLeaf && !readOnly ? "button" : undefined}
                aria-label={isLeaf && !readOnly ? jqPath(path) : undefined}
                data-leaf-focusable={isLeaf && !readOnly ? "true" : undefined}
                onKeyDown={isLeaf && !readOnly ? (e) => {
                    if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onLeafActivate?.(path); }
                } : undefined}
                className={`flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-mono select-none focus:outline-none focus:ring-2 focus:ring-dark-green ${isLeaf && !readOnly ? "cursor-grab hover:bg-light-blue" : "cursor-default"}`}
                style={{ paddingLeft: depth * 16 + 6 }}
            >
                {hasChildren ? (
                    <span onClick={() => onToggle(pathKey)} className="cursor-pointer w-3.5 text-center text-medium-gray text-[10px]">
                        {collapsed.has(pathKey) ? "▶" : "▼"}
                    </span>
                ) : (
                    <span className="w-3.5 text-center text-[10px]" style={{ color: "#b0c4f7" }}>⠿</span>
                )}
                <span className="text-off-black font-medium">{name}</span>
                <span className="text-medium-gray">:</span>
                {isLeaf && <span className="max-w-[180px] overflow-hidden text-ellipsis whitespace-nowrap" style={{ color: typeColor }}>{displayValue}</span>}
                {!isLeaf && <span className="text-medium-gray text-[11px]">{displayValue}</span>}
                {isLeaf && !readOnly && <span className="ml-auto text-[10px] text-medium-gray">{t("drag →")}</span>}
            </div>
            {isObject && !collapsed.has(pathKey) && Object.entries(value as Record<string, any>).map(([childKey, childValue]) => (
                <TreeNode key={childKey} name={childKey} value={childValue} path={[...path, childKey]} depth={depth + 1} collapsed={collapsed} onToggle={onToggle} readOnly={readOnly} dragPathRef={dragPathRef} onLeafActivate={onLeafActivate} pickActive={pickActive} />
            ))}
            {isArray && !collapsed.has(pathKey) && (value as any[]).map((item, index) => (
                <TreeNode key={index} name={`[${index}]`} value={item} path={[...path, String(index)]} depth={depth + 1} collapsed={collapsed} onToggle={onToggle} readOnly={readOnly} dragPathRef={dragPathRef} onLeafActivate={onLeafActivate} pickActive={pickActive} />
            ))}
        </div>
    );
}

function DropSlot({ label, mapping, sampleInput, onDrop, onClear, onRemove, readOnly, dragPathRef, required, onStartPick }: {
    label: string;
    mapping: string[] | null;
    sampleInput: any;
    onDrop: (path: string[]) => void;
    onClear: () => void;
    onRemove?: () => void;
    readOnly: boolean;
    dragPathRef: DragPathRef;
    required?: boolean;
    onStartPick?: StartPick;
}) {
    const { isOver, handlers, resetOver } = useDragOver(readOnly);
    const previewValue = mapping ? valAt(sampleInput, mapping) : undefined;
    const pathNotInSample = mapping && previewValue === undefined;
    const { t } = useTranslation();

    return (
        <div data-slot-row tabIndex={-1} className="flex items-center gap-2 py-1.5 border-b border-divider-lines last:border-b-0 overflow-hidden focus:outline-none focus:ring-2 focus:ring-dark-green focus:ring-inset rounded">
            <span className="font-mono text-xs text-secondary-gray font-medium flex items-center gap-1.5 shrink-0" style={{ minWidth: 130 }}>
                {label}
                {required && <span className="text-orange font-bold ml-0.5" title={t("Required")} aria-label={t("Required")}>*</span>}
            </span>
            <span className="text-medium-gray shrink-0">←</span>
            {mapping ? (
                <div className={`${pathNotInSample ? MAPPING_DISPLAY_WARNING : MAPPING_DISPLAY} overflow-hidden`}>
                    <span className="flex-1 truncate min-w-0" style={{ direction: 'rtl', textAlign: 'left' }} title={jqPath(mapping)}><bdi>{jqPath(mapping)}</bdi></span>
                    {pathNotInSample ? (
                        <span className="text-[10px] italic whitespace-nowrap">{t("Not in schema")}</span>
                    ) : (
                        <span className="text-medium-gray text-[11px] max-w-[120px] overflow-hidden text-ellipsis whitespace-nowrap">= {JSON.stringify(previewValue)}</span>
                    )}
                    {!readOnly && <button onClick={onClear} className="text-orange hover:text-logs-orange text-base leading-none px-0.5">×</button>}
                </div>
            ) : (
                <div
                    {...handlers}
                    tabIndex={readOnly ? undefined : 0}
                    role={readOnly ? undefined : "button"}
                    aria-label={readOnly ? undefined : t("Drag a source field here")}
                    onKeyDown={readOnly ? undefined : (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onStartPick?.(onDrop, e.currentTarget); } }}
                    onDrop={readOnly ? undefined : (e) => { e.preventDefault(); resetOver(); if (dragPathRef.current) onDrop(dragPathRef.current); }}
                    className={`${DROP_ZONE} focus:outline-none focus:ring-2 focus:ring-dark-green ${isOver ? DROP_ZONE_ACTIVE : DROP_ZONE_IDLE}`}
                >
                    {isOver ? t("Drop here") : t("Drag a source field here")}
                </div>
            )}
            {!readOnly && onRemove && (
                <button onClick={onRemove} className="text-orange hover:text-logs-orange text-lg leading-none shrink-0">×</button>
            )}
        </div>
    );
}

function AdditionalRow({ field, index, onUpdate, onRemove, sampleInput, readOnly, dragPathRef, onStartPick }: {
    field: AdditionalField;
    index: number;
    onUpdate: (index: number, field: AdditionalField) => void;
    onRemove: (index: number) => void;
    sampleInput: any;
    readOnly: boolean;
    dragPathRef: DragPathRef;
    onStartPick?: StartPick;
}) {
    const { isOver, handlers, resetOver } = useDragOver(readOnly);
    const pathSegments = field.path ? field.path.split(".") : null;
    const previewValue = pathSegments ? valAt(sampleInput, pathSegments) : undefined;
    const pathNotInSample = field.path && previewValue === undefined;
    const { t } = useTranslation();

    return (
        <div data-slot-row tabIndex={-1} className="flex gap-2 items-center mb-1.5 focus:outline-none focus:ring-2 focus:ring-dark-green focus:ring-inset rounded">
            <input
                value={field.name}
                onChange={e => onUpdate(index, { ...field, name: e.target.value })}
                placeholder={t("field name")}
                disabled={readOnly}
                className={`${inputClasses.standard} font-mono text-xs w-[130px] shrink-0`}
            />
            <span className="text-medium-gray shrink-0">←</span>
            {field.path ? (
                <div className={`${pathNotInSample ? MAPPING_DISPLAY_WARNING : MAPPING_DISPLAY} overflow-hidden`}>
                    <span className="flex-1 truncate min-w-0" style={{ direction: 'rtl', textAlign: 'left' }} title={jqPath(field.path.split("."))}><bdi>{jqPath(field.path.split("."))}</bdi></span>
                    {pathNotInSample ? (
                        <span className="text-[10px] italic whitespace-nowrap">{t("Not in schema")}</span>
                    ) : (
                        <span className="text-medium-gray text-[11px] max-w-[90px] overflow-hidden text-ellipsis whitespace-nowrap">= {JSON.stringify(previewValue)}</span>
                    )}
                    {!readOnly && <button onClick={() => onUpdate(index, { ...field, path: "" })} className="text-orange hover:text-logs-orange text-base leading-none">×</button>}
                </div>
            ) : (
                <div
                    {...handlers}
                    tabIndex={readOnly ? undefined : 0}
                    role={readOnly ? undefined : "button"}
                    aria-label={readOnly ? undefined : t("Drag source field")}
                    onKeyDown={readOnly ? undefined : (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onStartPick?.((p) => onUpdate(index, { ...field, path: p.join(".") }), e.currentTarget); } }}
                    onDrop={readOnly ? undefined : (e) => { e.preventDefault(); resetOver(); if (dragPathRef.current) onUpdate(index, { ...field, path: dragPathRef.current.join(".") }); }}
                    className={`${DROP_ZONE} focus:outline-none focus:ring-2 focus:ring-dark-green ${isOver ? DROP_ZONE_ACTIVE : DROP_ZONE_IDLE}`}
                >
                    <span className="flex-1 text-medium-gray text-[11px] max-w-[90px] overflow-hidden text-ellipsis whitespace-nowrap">{isOver ? t("Drop here") : t("Drag source field")}</span>
                </div>
            )}
            {!field.path && !readOnly && (<button onClick={() => onRemove(index)} className="text-orange hover:text-logs-orange text-base leading-none">×</button>)}
        </div>
    );
}

function ConditionRow({ condition, index, onUpdate, onRemove, readOnly, dragPathRef, onStartPick }: {
    condition: Condition;
    index: number;
    onUpdate: (index: number, condition: Condition) => void;
    onRemove: (index: number) => void;
    readOnly: boolean;
    dragPathRef: DragPathRef;
    onStartPick?: StartPick;
}) {
    const { isOver, handlers, resetOver } = useDragOver(readOnly);
    const { t } = useTranslation();
    const requiresValue = !["not_null", "is_null"].includes(condition.op);

    return (
        <div data-slot-row tabIndex={-1} className="flex gap-1.5 items-center mb-1.5 flex-wrap focus:outline-none focus:ring-2 focus:ring-dark-green focus:ring-inset rounded">
            {condition.field ? (
                <div className="bg-light-blue rounded px-2 py-0.5 font-mono text-xs text-dark-blue-800 flex items-center gap-1 min-w-0 overflow-hidden">
                    <span className="truncate min-w-0" style={{ direction: 'rtl', textAlign: 'left' }} title={jqPath(condition.field.split("."))}><bdi>{jqPath(condition.field.split("."))}</bdi></span>
                    {!readOnly && <button onClick={() => onUpdate(index, { ...condition, field: "" })} className="text-medium-gray hover:text-off-black text-sm leading-none shrink-0">×</button>}
                </div>
            ) : (
                <div
                    {...handlers}
                    tabIndex={readOnly ? undefined : 0}
                    role={readOnly ? undefined : "button"}
                    aria-label={readOnly ? undefined : t("Drag field")}
                    onKeyDown={readOnly ? undefined : (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onStartPick?.((p) => onUpdate(index, { ...condition, field: p.join(".") }), e.currentTarget); } }}
                    onDrop={readOnly ? undefined : (e) => { e.preventDefault(); resetOver(); if (dragPathRef.current) onUpdate(index, { ...condition, field: dragPathRef.current.join(".") }); }}
                    className={`border-2 border-dashed rounded px-2.5 py-0.5 text-xs text-center transition-all min-w-[120px] focus:outline-none focus:ring-2 focus:ring-dark-green ${isOver ? DROP_ZONE_ACTIVE : DROP_ZONE_IDLE}`}
                >
                    {isOver ? t("Drop here") : t("Drag field")}
                </div>
            )}
            <SelectMenu
                value={condition.op}
                options={OPS.map(op => ({ value: op.v, label: t(op.labelKey as any) }))}
                onChange={v => onUpdate(index, { ...condition, op: v })}
                disabled={readOnly}
                className="w-40"
                compact
                ariaLabel={t("Operator")}
            />
            {requiresValue && (
                <input
                    value={condition.value || ""}
                    onChange={e => onUpdate(index, { ...condition, value: e.target.value })}
                    placeholder={t("value")}
                    disabled={readOnly}
                    className={`${inputClasses.standard} font-mono text-xs w-24`}
                />
            )}
            {!readOnly && (
                <button onClick={() => onRemove(index)} className="text-orange hover:text-logs-orange text-lg leading-none">×</button>
            )}
        </div>
    );
}

function LockedFieldSlot({ label, expression, required }: {
    label: string;
    expression: string;
    required?: boolean;
}) {
    const { t } = useTranslation();
    const hasValue = expression.trim().length > 0;
    return (
        <div className="flex items-center gap-2 py-1.5 border-b border-divider-lines last:border-b-0">
            <span className="font-mono text-xs text-secondary-gray font-medium flex items-center gap-1.5" style={{ minWidth: 130 }}>
                {label}
                {required && <span className="text-orange font-bold ml-0.5" title={t("Required")} aria-label={t("Required")}>*</span>}
            </span>
            <input
                type="text"
                value={hasValue ? expression : ""}
                placeholder={hasValue ? undefined : t("Not mapped")}
                disabled
                title={hasValue ? expression : undefined}
                className={`${inputClasses.standard} text-xs flex-1 font-mono bg-very-light-gray text-medium-gray`}
            />
            <span className="text-[10px] text-medium-gray italic whitespace-nowrap shrink-0">{t("Edit in Code mode")}</span>
        </div>
    );
}

// ── Main component ──

export type JqFilterBuilderProps = {
    onGenerate: (jq: string) => void;
    onCodeChange?: (jq: string) => void;
    onSampleInputChange?: (sample: object) => void;
    readOnly?: boolean;
    initialJq?: string;
    initialSampleInput?: object;
    objectType?: string;
};

const JqFilterBuilder = ({ onGenerate, onCodeChange, onSampleInputChange, readOnly = false, initialJq, initialSampleInput, objectType = "obv" }: JqFilterBuilderProps) => {
    const { t } = useTranslation();
    const dragPathRef = useRef<string[] | null>(null);
    const jqEditorRef = useRef<editor.IStandaloneCodeEditor | null>(null);
    const hasInteracted = useRef(false);
    const codeEditedByUser = useRef(false);
    const codeSyncTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const onGenerateRef = useRef(onGenerate);
    onGenerateRef.current = onGenerate;

    // ── Keyboard mapping (a11y) ──
    // Mirrors drag-and-drop for keyboard users: pressing Enter on a drop slot hands its
    // "pick" callback here and moves focus to the first source leaf; pressing Enter on a
    // leaf invokes that callback with the leaf's path. Esc (handled on the tree) cancels.
    const sourceTreeRef = useRef<HTMLDivElement>(null);
    const pendingPickRef = useRef<((path: string[]) => void) | null>(null);
    const lastSlotRef = useRef<HTMLElement | null>(null);
    const [pickActive, setPickActive] = useState(false);

    const startKeyboardPick = useCallback<StartPick>((onPick, slotEl) => {
        const firstLeaf = sourceTreeRef.current?.querySelector<HTMLElement>('[data-leaf-focusable="true"]');
        if (!firstLeaf) return; // no selectable source fields — nothing to pick
        pendingPickRef.current = onPick;
        lastSlotRef.current = slotEl;
        setPickActive(true);
        firstLeaf.focus();
    }, []);

    const completeKeyboardPick = useCallback((path: string[]) => {
        const pick = pendingPickRef.current;
        if (!pick) return;
        // The drop zone unmounts once mapped (replaced by the mapping display), so return
        // focus to its persistent row wrapper instead — keeping the user on the right side.
        const slotEl = lastSlotRef.current;
        const row = (slotEl?.closest('[data-slot-row]') as HTMLElement | null) ?? slotEl;
        pendingPickRef.current = null;
        lastSlotRef.current = null;
        setPickActive(false);
        pick(path);
        row?.focus();
    }, []);

    const cancelKeyboardPick = useCallback(() => {
        if (!pendingPickRef.current) return;
        pendingPickRef.current = null;
        setPickActive(false);
        lastSlotRef.current?.focus();
        lastSlotRef.current = null;
    }, []);

    // ── Source payload state ──
    const [sampleInput, setSampleInput] = useState<any>(initialSampleInput ?? {});
    const [rawJsonInput, setRawJsonInput] = useState("");
    const [isEditingJson, setIsEditingJson] = useState(false);
    const [jsonParseError, setJsonParseError] = useState<string | null>(null);
    const [collapsedNodes, setCollapsedNodes] = useState<Set<string>>(new Set());

    // ── Builder state (initialized from existing jq if available) ──
    const [parsedInitial] = useState<ParseResult | null>(() => {
        const literalKeys = new Set(
            (GUNDI_OBJECT_SPECS[objectType]?.fields ?? []).filter(f => f.literalInput).map(f => f.key)
        );
        return parseJqToBuilderState(initialJq ?? "", literalKeys);
    });
    const [isComplexJq, setIsComplexJq] = useState(() => parsedInitial != null && !parsedInitial.isFullyParseable);
    const [complexFields, setComplexFields] = useState<Record<string, string>>(() => parsedInitial?.complexFields ?? {});

    const [coreFieldMaps, setCoreFieldMaps] = useState<CoreFieldMaps>(() => parsedInitial?.maps ?? {});
    const [literalMaps, setLiteralMaps] = useState<Record<string, string>>(() => parsedInitial?.literalMaps ?? {});
    const [additionalFields, setAdditionalFields] = useState<AdditionalField[]>(() => {
        const parsed = parsedInitial?.addl ?? [];
        return parsed.length > 0 ? parsed : [{ name: "", path: "" }];
    });
    const [conditions, setConditions] = useState<Condition[]>(() => parsedInitial?.conds ?? []);

    // ── Code editor state (auto-switch to Code for complex jq) ──
    const [isCodeMode, setIsCodeMode] = useState(() => isComplexJq);
    const [codeText, setCodeText] = useState(initialJq ?? "");

    // ── Sync initial sample input from props ──
    useEffect(() => {
        if (initialSampleInput && Object.keys(initialSampleInput).length > 0) {
            setSampleInput(initialSampleInput);
        }
    }, [initialSampleInput]);

    // Core and additional field mappings keep their references when the sample loses a path —
    // DropSlot / AdditionalRow surface a "Not in schema" warning so the user can decide whether
    // to clear them. Conditions don't have an equivalent affordance, so they are still cleared.
    useEffect(() => {
        if (Object.keys(sampleInput).length === 0) return;
        const validPaths = new Set(flatPaths(sampleInput).map(p => p.path.join(".")));

        setConditions(prev => {
            const next = prev.map(c => c.field && !validPaths.has(c.field) ? { ...c, field: "" } : c);
            return next.some((c, i) => c !== prev[i]) ? next : prev;
        });
    }, [sampleInput]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Derived values ──
    const generatedJq = useMemo(() => genJq(conditions, coreFieldMaps, additionalFields, objectType, literalMaps), [conditions, coreFieldMaps, additionalFields, objectType, literalMaps]);
    const evalResult = useMemo(() => evalMapping(sampleInput, coreFieldMaps, additionalFields, conditions, objectType, literalMaps, complexFields), [sampleInput, coreFieldMaps, additionalFields, conditions, objectType, literalMaps, complexFields]);
    const outputPreview = evalResult.data ? [evalResult.data] : [];
    const outputErrors = useMemo(() => {
        if (!evalResult.data) return [];
        return validateMappedOutput(evalResult.data, objectType);
    }, [evalResult.data, objectType]);

    // ── Notify parent when jq changes (only after user interaction, never for complex jq) ──
    useEffect(() => {
        if (hasInteracted.current && !isComplexJq) onGenerateRef.current(generatedJq);
    }, [generatedJq, isComplexJq]);

    // ── Object type spec & optional fields ──
    const spec = GUNDI_OBJECT_SPECS[objectType];
    const optionalFields = useMemo(() => spec ? spec.fields.filter(f => !f.required) : [], [spec]);
    const [activeOptionalKeys, setActiveOptionalKeys] = useState<Set<string>>(() => {
        const initial = new Set<string>();
        for (const f of optionalFields) {
            if (coreFieldMaps[f.key]) initial.add(f.key);
        }
        return initial;
    });

    // Only prune coreFieldMaps when the user CHANGES objectType, not on initial mount.
    // On initial mount the maps come from the parser and may contain valid fields
    // not in the current spec (e.g. source in an event jq, or title in an observation jq).
    const isInitialMount = useRef(true);
    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
            // Still seed activeOptionalKeys from initial maps
            const nextActiveOptionalKeys = new Set<string>();
            for (const f of optionalFields) {
                if (coreFieldMaps[f.key]) nextActiveOptionalKeys.add(f.key);
            }
            setActiveOptionalKeys(nextActiveOptionalKeys);
            return;
        }

        if (!spec) {
            setActiveOptionalKeys(new Set());
            setCoreFieldMaps(() => ({} as CoreFieldMaps));
            return;
        }

        // Clear core fields, literals, and conditions that don't belong to the new object type.
        // Additional fields are NOT touched — they are user-defined and type-independent.
        markInteracted();
        setCoreFieldMaps({} as CoreFieldMaps);
        setLiteralMaps({});
        setConditions([]);
        setActiveOptionalKeys(new Set());

    }, [objectType, spec, optionalFields]); // eslint-disable-line react-hooks/exhaustive-deps

    // Sync code editor when generatedJq changes (e.g. after objectType prune)
    useEffect(() => {
        if (!isComplexJq && !codeEditedByUser.current) setCodeText(generatedJq);
    }, [generatedJq]); // eslint-disable-line react-hooks/exhaustive-deps

    // Sync builder state from code editor edits (debounced to avoid parsing mid-keystroke)
    useEffect(() => {
        if (!codeEditedByUser.current) return;
        codeEditedByUser.current = false;

        if (codeSyncTimer.current) clearTimeout(codeSyncTimer.current);
        codeSyncTimer.current = setTimeout(() => {
            const literalKeys = new Set(
                (GUNDI_OBJECT_SPECS[objectType]?.fields ?? []).filter(f => f.literalInput).map(f => f.key)
            );
            const parsed = parseJqToBuilderState(codeText, literalKeys);
            if (!parsed) {
                // Empty or fully unparseable — clear builder
                setCoreFieldMaps({} as CoreFieldMaps);
                setLiteralMaps({});
                setAdditionalFields([{ name: "", path: "" }]);
                setConditions([]);
                setComplexFields({});
                setIsComplexJq(false);
                return;
            }
            setCoreFieldMaps(parsed.maps);
            setLiteralMaps(parsed.literalMaps);
            setConditions(parsed.conds);
            setAdditionalFields(parsed.addl.length > 0 ? parsed.addl : [{ name: "", path: "" }]);
            setComplexFields(parsed.complexFields);
            setIsComplexJq(!parsed.isFullyParseable);
        }, 400);

        return () => { if (codeSyncTimer.current) clearTimeout(codeSyncTimer.current); };
    }, [codeText, objectType]); // eslint-disable-line react-hooks/exhaustive-deps

    const activeOptionalFields = optionalFields.filter(f => activeOptionalKeys.has(f.key));
    const hasMappings = Object.values(coreFieldMaps).some(v => v != null) || additionalFields.some(f => f.name && f.path) || Object.values(literalMaps).some(v => v.trim());

    // ── Interaction helpers ──
    const markInteracted = () => { hasInteracted.current = true; };

    const updateCoreFieldMap = (key: string, path: string[] | null) => {
        markInteracted();
        setCoreFieldMaps(prev => ({ ...prev, [key]: path }));
    };

    const addCondition = () => {
        markInteracted();
        setConditions(prev => [...prev, { field: "", op: "not_null", value: "" }]);
    };
    const updateCondition = (index: number, updated: Condition) => {
        markInteracted();
        setConditions(prev => prev.map((c, i) => i === index ? updated : c));
    };
    const removeCondition = (index: number) => {
        markInteracted();
        setConditions(prev => prev.filter((_, i) => i !== index));
    };

    const addAdditionalField = () => {
        markInteracted();
        setAdditionalFields(prev => [...prev, { name: "", path: "" }]);
    };
    const updateAdditionalField = (index: number, updated: AdditionalField) => {
        markInteracted();
        setAdditionalFields(prev => prev.map((f, i) => i === index ? updated : f));
    };
    const removeAdditionalField = (index: number) => {
        markInteracted();
        setAdditionalFields(prev => prev.filter((_, i) => i !== index));
    };

    const activateOptionalField = (key: string) => {
        markInteracted();
        setActiveOptionalKeys(prev => new Set([...prev, key]));
    };
    const deactivateOptionalField = (key: string) => {
        markInteracted();
        setActiveOptionalKeys(prev => { const next = new Set(prev); next.delete(key); return next; });
        setCoreFieldMaps(prev => { const next = { ...prev }; delete next[key]; return next; });
    };

    const toggleCollapsed = useCallback((key: string) => setCollapsedNodes(prev => {
        const next = new Set(prev);
        next.has(key) ? next.delete(key) : next.add(key);
        return next;
    }), []);

    const applyJsonEdit = () => {
        try {
            const parsed = JSON.parse(rawJsonInput);
            setSampleInput(parsed);
            onSampleInputChange?.(parsed);
            setJsonParseError(null);
            setIsEditingJson(false);
        } catch {
            setJsonParseError(t("Invalid JSON"));
        }
    };

    const toggleCodeMode = () => {
        const next = !isCodeMode;
        if (next) setCodeText(isComplexJq ? (initialJq ?? "") : generatedJq);
        setIsCodeMode(next);
    };

    // Complex fields are individually locked via LockedFieldSlot; simple fields remain editable

    // ── Render ──
    return (
        <div className="flex flex-col">
            <div className="grid grid-cols-2 gap-3">

                {/* ── Source Payload Panel ── */}
                <div className={PANEL_CLASSES}>
                    <div className={HEADER_CLASSES}>
                        <span>{t("Source Payload")}</span>
                        {!readOnly && (
                            <SegmentedToggle
                                value={isEditingJson ? "edit" : "view"}
                                options={[{ value: "view", label: t("View") }, { value: "edit", label: t("Edit") }]}
                                onChange={(v) => {
                                    const editing = v === "edit";
                                    setIsEditingJson(editing);
                                    if (editing) setRawJsonInput(JSON.stringify(sampleInput, null, 2));
                                    else setJsonParseError(null);
                                }}
                            />
                        )}
                    </div>
                    {isEditingJson ? (
                        <div className="p-2.5 flex flex-col gap-2 flex-1">
                            <textarea
                                value={rawJsonInput}
                                onChange={e => setRawJsonInput(e.target.value)}
                                className={`${inputClasses.standard} flex-1 font-mono text-xs resize-none min-h-[160px]`}
                            />
                            {jsonParseError && <div className="text-sm text-status-red">{jsonParseError}</div>}
                            <div className="flex gap-3 justify-end">
                                <button onClick={() => { setIsEditingJson(false); setJsonParseError(null); }} className={buttonClasses.cancel} type="button">{t("Cancel")}</button>
                                <button
                                    onClick={applyJsonEdit}
                                    className={buttonClasses.lightGreen}
                                    type="button"
                                >{t("Apply")}</button>
                            </div>
                        </div>
                    ) : (
                        <div className="flex flex-col flex-1" style={{ height: 360 }}>
                            <div
                                ref={sourceTreeRef}
                                onKeyDown={(e) => { if (e.key === "Escape" && pickActive) { e.preventDefault(); cancelKeyboardPick(); } }}
                                className={`py-1.5 overflow-y-auto flex-1 ${pickActive ? "ring-2 ring-inset ring-dark-green rounded" : ""}`}
                            >
                                {Object.keys(sampleInput).length === 0 ? (
                                    <div className="px-3.5 py-2 text-xs text-medium-gray italic">
                                        {!readOnly ? t('Click "Edit" to add sample payload') : t("No sample payload")}
                                    </div>
                                ) : (
                                    Object.entries(sampleInput).map(([key, value]) => (
                                        <TreeNode key={key} name={key} value={value} path={[key]} depth={0} collapsed={collapsedNodes} onToggle={toggleCollapsed} readOnly={readOnly} dragPathRef={dragPathRef} onLeafActivate={completeKeyboardPick} pickActive={pickActive} />
                                    ))
                                )}
                            </div>
                            {Object.keys(sampleInput).length > 0 && (
                                <div className="mt-auto px-3.5 py-2 bg-very-light-gray border-t border-divider-lines text-[11px] text-medium-gray flex items-center gap-1.5">
                                    {pickActive ? (
                                        <span className="text-dark-green font-medium">{t("Press Enter on a field to select it, or Esc to cancel")}</span>
                                    ) : (
                                        <><span style={{ color: "#b0c4f7" }}>⠿</span> {t("Drag leaf fields onto mapping slots on the right")}</>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* ── Field Mappings / Code Editor Panel ── */}
                <div className={PANEL_CLASSES}>
                    <div className={HEADER_CLASSES}>
                        <span>{t("Field Mappings")}</span>
                        <SegmentedToggle
                            value={isCodeMode ? "code" : "builder"}
                            options={[{ value: "code", label: t("Code") }, { value: "builder", label: t("Builder") }]}
                            onChange={() => toggleCodeMode()}
                        />
                    </div>
                    {isCodeMode ? (
                        <>
                            {isComplexJq && (
                                <div className="px-3.5 py-2 bg-light-blue text-xs text-dark-blue-800 border-b border-divider-lines">
                                    {t("This filter contains complex expressions. Simplify the filter to use the visual Builder.")}
                                </div>
                            )}
                            <div style={{ height: 360, resize: "vertical", overflow: "hidden" }}>
                                <Editor
                                    height="100%"
                                    language="jq"
                                    value={codeText}
                                    theme="vs"
                                    options={{ minimap: { enabled: false }, scrollBeyondLastLine: false, fontSize: 13, lineHeight: 20, padding: { top: 8 }, readOnly, lineNumbers: "on", wordWrap: "on", automaticLayout: true, renderLineHighlight: "none", fontFamily: "'SF Mono', 'Fira Code', 'Cascadia Code', monospace" }}
                                    beforeMount={registerJqLanguage}
                                    onMount={(e) => { jqEditorRef.current = e; }}
                                    onChange={(v) => { codeEditedByUser.current = true; setCodeText(v ?? ""); onCodeChange?.(v ?? ""); }}
                                />
                            </div>
                        </>
                    ) : (
                        <div className="px-3.5 py-3 overflow-y-auto flex-1">

                            {/* Warning banner for complex jq in builder mode */}
                            {isComplexJq && (
                                <div className="mb-3 px-3 py-2 rounded-md bg-very-light-orange text-xs text-orange">
                                    {t("Some fields use complex expressions that can only be edited in Code mode.")}
                                </div>
                            )}

                            {/* Conditions Section */}
                            <div className="mb-4">
                                <div className="flex items-center justify-between mb-2">
                                    <span className={SECTION_LABEL}>{t("Conditions")}</span>
                                    {!readOnly && <button onClick={addCondition} className={buttonClasses.lightGreen} type="button">{t("Add Condition")}</button>}
                                </div>
                                {conditions.length === 0 && <div className="text-xs text-medium-gray">{t("No conditions — all records pass through")}</div>}
                                {conditions.map((condition, index) => (
                                    <ConditionRow key={index} condition={condition} index={index} readOnly={readOnly} dragPathRef={dragPathRef} onUpdate={updateCondition} onRemove={removeCondition} onStartPick={startKeyboardPick} />
                                ))}
                            </div>

                            {/* Core Fields Section */}
                            <div className="mb-4">
                                <span className={SECTION_LABEL}>{t("Core Fields")}</span>
                                {spec ? spec.fields.filter(f => f.required || f.showInCore).map(f => {
                                    if (complexFields[f.key]) {
                                        return <LockedFieldSlot key={f.key} label={f.labelKey} expression={complexFields[f.key]} required={f.required} />;
                                    }
                                    if (f.literalInput) {
                                        return (
                                            <div key={f.key} className="flex items-center gap-2 py-1.5 border-b border-divider-lines last:border-b-0">
                                                <span className="font-mono text-xs text-secondary-gray font-medium flex items-center gap-1.5 shrink-0" style={{ minWidth: 130 }}>
                                                    {f.labelKey}
                                                    {f.required && <span className="text-orange font-bold ml-0.5" title={t("Required")} aria-label={t("Required")}>*</span>}
                                                </span>
                                                <span className="text-medium-gray shrink-0">←</span>
                                                <input
                                                    type="text"
                                                    value={literalMaps[f.key] ?? ""}
                                                    onChange={e => { markInteracted(); setLiteralMaps(prev => ({ ...prev, [f.key]: e.target.value })); }}
                                                    placeholder={f.description}
                                                    disabled={readOnly}
                                                    className={`${inputClasses.standard} text-xs flex-1 font-mono`}
                                                />
                                            </div>
                                        );
                                    }
                                    return (
                                        <DropSlot key={f.key} label={f.labelKey} required={f.required} mapping={coreFieldMaps[f.key] ?? null} sampleInput={sampleInput} readOnly={readOnly} dragPathRef={dragPathRef}
                                            onDrop={p => updateCoreFieldMap(f.key, p)} onClear={() => updateCoreFieldMap(f.key, null)} onStartPick={startKeyboardPick} />
                                    );
                                }) : (
                                    <div className="text-xs text-medium-gray">{t("Select an object type to see required fields")}</div>
                                )}
                            </div>

                            {/* Additional Fields Section */}
                            <div>
                                <div className="flex items-center justify-between mb-2">
                                    <span className={SECTION_LABEL}>{t("Additional Fields")}</span>
                                    {!readOnly && <button onClick={addAdditionalField} className={buttonClasses.lightGreen} type="button">{t("Add Field")}</button>}
                                </div>


                                {additionalFields.map((field, index) => (
                                    <AdditionalRow key={`custom-${index}`} field={field} index={index} sampleInput={sampleInput} readOnly={readOnly} dragPathRef={dragPathRef}
                                        onUpdate={updateAdditionalField} onRemove={removeAdditionalField} onStartPick={startKeyboardPick} />
                                ))}

                                {/* Complex additional fields (locked) */}
                                {Object.entries(complexFields)
                                    .filter(([key]) => key.startsWith("addl:"))
                                    .map(([key, expr]) => (
                                        <LockedFieldSlot key={key} label={key.slice(5)} expression={expr} />
                                    ))}

                                {activeOptionalFields.length === 0 && additionalFields.length === 0
                                    && !Object.keys(complexFields).some(k => k.startsWith("addl:"))
                                    && (
                                        <div className="text-xs text-medium-gray">{t("No additional fields")}</div>
                                    )}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* ── Output Preview ── */}
            <div className="mt-3 rounded-lg border border-divider-lines overflow-hidden flex flex-col">
                <div className={`${HEADER_CLASSES} flex-shrink-0`}>
                    <span>{t("Output Preview")}</span>
                    {!hasMappings && <span className="text-xs text-medium-gray font-normal">{t("Map some fields to see output")}</span>}
                </div>
                {hasMappings && evalResult.failedConditions.length > 0 ? (
                    <div className="px-4 py-3 bg-very-light-orange text-sm">
                        <p className="font-medium text-orange mb-1">{t("Conditions not met — record would be skipped")}</p>
                        <ul className="list-disc list-inside text-xs text-secondary-gray">
                            {evalResult.failedConditions.map((description, index) => <li key={index} className="font-mono">{description}</li>)}
                        </ul>
                    </div>
                ) : (
                    <>
                        {hasMappings && outputErrors.length > 0 && (
                            <div className="border-b border-divider-lines px-4 py-3 bg-very-light-orange">
                                <p className="text-xs font-semibold mb-1.5 text-orange">{t("Validation errors")}</p>
                                <ul className="flex flex-col gap-1">
                                    {outputErrors.map((error, index) => (
                                        <li key={index} className="text-xs text-secondary-gray flex items-center gap-1.5">
                                            <span className="font-mono text-orange">{error.field}</span>
                                            <span className="text-medium-gray">—</span>
                                            <span>{String(t(error.messageKey as any, error.messageParams as any))}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        <pre className="font-mono text-xs p-4 bg-[#F9F9F9] text-off-black whitespace-pre-wrap min-h-[60px] leading-relaxed m-0">
                            {hasMappings ? JSON.stringify(outputPreview, null, 2) : ""}
                        </pre>
                    </>
                )}
            </div>
        </div>
    );
};

export default JqFilterBuilder;
